/**
 * Cache clearing utilities for logout and data cleanup
 */

/**
 * Clears all application caches including Firestore, browser storage, and service worker caches
 * @param firestore - Firestore instance to clear
 */
export const clearAllCaches = async (firestore?: any): Promise<void> => {
  const clearPromises: Promise<void>[] = []

  // Clear Firestore cache
  if (firestore) {
    clearPromises.push(clearFirestoreCache(firestore))
  }

  // Clear browser storage
  clearPromises.push(clearBrowserStorage())

  // Clear service worker caches
  clearPromises.push(clearServiceWorkerCaches())

  // Execute all cache clearing operations in parallel
  await Promise.allSettled(clearPromises)
}

/**
 * Clears Firestore offline persistence cache
 */
const clearFirestoreCache = async (firestore: any): Promise<void> => {
  try {
    const { clearIndexedDbPersistence, terminate } = await import(
      'firebase/firestore'
    )

    // Terminate the Firestore instance to clear cache
    await terminate(firestore)

    // Clear IndexedDB persistence cache
    await clearIndexedDbPersistence(firestore)
  } catch (error) {
    console.warn('Could not clear Firestore cache:', error)
  }
}

/**
 * Clears browser localStorage and sessionStorage
 */
const clearBrowserStorage = async (): Promise<void> => {
  try {
    localStorage.clear()
    sessionStorage.clear()
  } catch (error) {
    console.warn('Could not clear browser storage:', error)
  }
}

/**
 * Clears service worker caches
 */
const clearServiceWorkerCaches = async (): Promise<void> => {
  if ('serviceWorker' in navigator && 'caches' in window) {
    try {
      const cacheNames = await caches.keys()
      await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)))
    } catch (error) {
      console.warn('Could not clear service worker cache:', error)
    }
  }
}

/**
 * Gets the initial/default session state
 */
export const getInitialSessionState = () => {
  const { formatDateToString } = require('./FormatHelpers')

  return {
    isAuthenticating: true,
    isLoadingData: false,
    user: null,
    desks: [],
    offices: [],
    selectedOffice: undefined,
    officeDesks: [],
    selectedFromDate: formatDateToString(new Date()),
    selectedRecurUnit: 'Once off' as const,
    selectedRecurAmount: 30,
    bookings: [],
    userBookings: [],
    deskToEdit: null,
    highlightedDates: []
  }
}
